import React, { useRef } from "react";
import Draggable from "react-draggable";
import { X, Copy, Check } from "lucide-react";
import { LOCATION_ID_TO_NAME } from "../../utils/constants";
import { getStatusColor } from "../../services/communicationCenterService";
import { isWeakNameLink, matchReasonLabel, senderLabel } from "../../utils/clientLink";

const ChainDetailModal = ({ chain, onClose }) => {
    const dragRef = useRef(null);
    const [copiedField, setCopiedField] = React.useState(null);

    if (!chain) return null;

    const copyToClipboard = (value, fieldName) => {
        navigator.clipboard.writeText(String(value));
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const formatTimestamp = (ts) => {
        if (!ts) return "—";
        const d = new Date(ts);
        return d.toLocaleString("en-US", {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            timeZoneName: "short",
        });
    };

    const DetailRow = ({ label, value, mono = false, copyable = false }) => (
        <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-36 shrink-0 pt-0.5">{label}</span>
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <span className={`text-sm text-gray-700 break-all ${mono ? "font-mono text-xs" : ""}`}>
                    {value ?? "—"}
                </span>
                {copyable && value && (
                    <button
                        onClick={() => copyToClipboard(value, label)}
                        className="!p-1 !bg-transparent !border-none !text-gray-300 hover:!text-gray-500 !transition-colors shrink-0"
                        title="Copy"
                    >
                        {copiedField === label ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                    </button>
                )}
            </div>
        </div>
    );

    const statusBadge = (status) => {
        const base = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide";
        return `${base} ${getStatusColor(status)}`;
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Draggable handle=".modal-drag-handle" nodeRef={dragRef}>
                <div
                    ref={dragRef}
                    className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
                    style={{ cursor: "default" }}
                >
                    {/* Header */}
                    <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 modal-drag-handle cursor-move select-none">
                        <h2 className="text-lg font-bold text-gray-800">Chain Details</h2>
                        <button
                            onClick={onClose}
                            className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="overflow-y-auto flex-grow px-6 py-4">
                        {/* Status badge */}
                        <div className="mb-4">
                            <span className={statusBadge(chain.status)}>{chain.status?.replace('_', ' ')}</span>
                            {chain.chain_type && chain.chain_type !== 'general' && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700">
                                    {chain.chain_type}
                                </span>
                            )}
                        </div>

                        {/* Identity */}
                        <div className="mb-5">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Identity</h3>
                            <DetailRow label="Chain UUID" value={chain.chain_id} mono copyable />
                            <DetailRow label="Client ID" value={chain.client_id} mono copyable />
                            <DetailRow label="Client Name" value={chain.client_name} />
                            <DetailRow label="Client Phone" value={chain.client_phone} mono copyable />
                            {chain.client_email && (
                                <DetailRow label="Client Email" value={chain.client_email} copyable />
                            )}
                            {senderLabel(chain) && (
                                <DetailRow label="Sender" value={senderLabel(chain)} />
                            )}
                            {(chain.sender_email && chain.sender_email !== chain.client_email) && (
                                <DetailRow label="Sender Email" value={chain.sender_email} copyable />
                            )}
                            {chain.client_match_reason && (
                                <DetailRow label="Match" value={matchReasonLabel(chain.client_match_reason)} />
                            )}
                            {isWeakNameLink(chain) && (
                                <DetailRow label="Link check" value="Last-name link needs confirmation" />
                            )}
                            {chain.suggested_client && (
                                <DetailRow
                                    label="Suggested"
                                    value={`${chain.suggested_client.client_name} (${chain.suggested_client.client_id})`}
                                />
                            )}
                        </div>

                        {/* Classification */}
                        <div className="mb-5">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Classification</h3>
                            <DetailRow label="Location" value={`${LOCATION_ID_TO_NAME[chain.location_id] || "Unknown"} (ID: ${chain.location_id})`} />
                            <DetailRow label="Chain Type" value={chain.chain_type || "general"} />
                            <DetailRow label="Status" value={chain.status} />
                            {chain.appointment_id && (
                                <DetailRow label="Appointment ID" value={chain.appointment_id} mono copyable />
                            )}
                            {chain.appointment_date && (
                                <DetailRow label="Appt Date" value={chain.appointment_date} />
                            )}
                            <DetailRow label="Events" value={chain.events?.length || 0} />
                        </div>

                        {/* Timing */}
                        <div className="mb-5">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Timing</h3>
                            <DetailRow label="Created" value={formatTimestamp(chain.created_at)} />
                            <DetailRow label="Updated" value={formatTimestamp(chain.updated_at)} />
                        </div>

                        {/* Notes */}
                        {chain.notes && (
                            <div className="mb-5">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Notes</h3>
                                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                    {chain.notes}
                                </div>
                            </div>
                        )}

                        {/* Client Lookup Log */}
                        {chain.client_lookup_log && chain.client_lookup_log.length > 0 && (
                            <div className="mb-5">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Client Lookup</h3>
                                <div className="space-y-1.5">
                                    {chain.client_lookup_log.map((entry, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${entry.matched ? 'bg-green-500' : 'bg-red-400'}`} />
                                            <span className="text-xs font-semibold text-gray-400 uppercase w-12 shrink-0">{entry.type}</span>
                                            <span className="font-mono text-xs text-gray-600 break-all">{entry.value}</span>
                                            {(entry.reason || (entry.type === "name" && entry.matched && !entry.reason)) && (
                                                <span className="text-xs text-gray-400 shrink-0">
                                                    {entry.reason ? matchReasonLabel(entry.reason) : "Name lookup"}
                                                </span>
                                            )}
                                            {entry.matched ? (
                                                <span className="text-xs text-green-600 font-medium shrink-0">→ client {entry.matched_client_id}</span>
                                            ) : entry.suggested_client_id ? (
                                                <span className="text-xs text-amber-600 font-medium shrink-0">suggested {entry.suggested_client_id}</span>
                                            ) : (
                                                <span className="text-xs text-red-400 shrink-0">no match</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Cancellation fee */}
                        {chain.cancellation_fee_status && chain.cancellation_fee_status !== 'not_applicable' && (
                            <div className="mb-5">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Cancellation Fee</h3>
                                <DetailRow label="Fee Status" value={chain.cancellation_fee_status?.replace(/_/g, ' ')} />
                            </div>
                        )}
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

export default ChainDetailModal;
